import { Device, until } from '@helpers/media';
import ExpandIcon from '@img/icons/expand.svg';
import { RootState } from '@state/ducks';
import ImageZoomModal from '@stories/Components/Media/ImageZoomModal/ImageZoomModal';
import GalleryIcon from '@stories/Elements/Media/GalleryIcon/GalleryIcon';
import LazyImage from '@stories/Elements/Media/LazyImage/LazyImage';
import Loader from '@stories/Elements/Misc/Loader/Loader.hybrid';
import ImageTransform from '@tti/image-transform';
import React, { lazy, ReactNode, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useLockBodyScroll } from 'react-use';
import { ProductMediaLayoutStyles as S } from './ProductMediaLayout.styles';

const ProductMediaLightbox = lazy(
  () => import('@stories/Widgets/ProductDetail/ProductMediaLightbox/ProductMediaLightbox'),
);

const FALLBACK_IMAGE = 'https://milwaukeetool.eu/SiteFiles/img/milwaukeenoImage.gif';

export enum ProductMediaLayoutOption {
  OneHero = 0,
  OneHeroOnePack = 1,
  MultipleHero = 2,
  TwoAppTwoHero = 4,
  TwoAppOneHero = 7,
  ThreeAppOneHero = 8,
  TwoHeroOneApp = 9,
  TwoAppOneHeroOneSnippet = 10,
  OneAppOneHeroOneSnippet = 11,
  OneAppOneHero = 12,
  OneHeroOneSnippet = 13,
  ThreeHeroOneApp = 14,
}

export interface ProductMediaItem {
  imageUrl: string;
  url: string | null;
  description: string | null;
  openInNewTab?: boolean;
}

export interface ProductAssets {
  layout: ProductMediaLayoutOption;
  hero: ProductMediaItem[];
  feature: ProductMediaItem[];
  app: ProductMediaItem[];
  appNoPadding: ProductMediaItem[];
  pack: ProductMediaItem[];
  snippet: ProductMediaItem | null;
  embed: ProductMediaItem | null;
}

export interface ProductMediaLayoutmedia {
  translations: Record<string, string | undefined>;
}

const ProductMediaLayout: React.FC<ProductMediaLayoutmedia> = (props) => {
  const media = useSelector((x: RootState) => x.productDetail.assets);
  const [showGallery, setShowGallery] = useState<boolean>(false);
  const [showEmbed, setShowEmbed] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  useLockBodyScroll(showGallery);

  const galleryAssets: string[] = useMemo(() => {
    if (!media) {
      return [];
    }

    return [
      ...media.hero.map((x) => x.imageUrl),
      ...media.feature.map((x) => x.imageUrl),
      ...media.app.map((x) => x.imageUrl),
    ];
  }, [media]);

  useEffect(() => {
    if (!videoRef) {
      return;
    }

    if (window.matchMedia(until(Device.TabletLarge)).matches) {
      return;
    }

    videoRef.current?.play();
  }, []);

  if (!media) {
    return null;
  }

  const renderImage = (
    media: ProductMediaItem | null,
    gallery?: number,
    single?: boolean,
    preload?: boolean,
  ): ReactNode => {
    if (!media) {
      return null;
    }

    let url = media.imageUrl;

    if (!url || url.length === 0) {
      url = FALLBACK_IMAGE;
    }

    if (url.toLowerCase().includes('hi_no_padding')) {
      // We want to handle whitespace cropping via entropy cropping //
      url = new ImageTransform(url.replace('hi_no_padding', 'hi')).entropyCrop().url;
    }

    return (
      <>
        {gallery ? (
          <S.MediaItemIcon onClick={() => setShowGallery(true)}>
            <GalleryIcon count={gallery} />
            <ExpandIcon />
          </S.MediaItemIcon>
        ) : null}

        <LazyImage src={url} width={single ? 871 : 443} height={single ? 871 : 443} preload={preload} />
      </>
    );
  };

  const renderInner = (): ReactNode => {
    switch (media.layout) {
      case ProductMediaLayoutOption.TwoAppOneHeroOneSnippet: {
        return (
          <S.MediaGrid>
            <S.MediaItem>{renderImage(media.app[0], undefined, false, true)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.app[1])}</S.MediaItem>
            <S.MediaItem>{renderImage(media.hero[0], galleryAssets.length)}</S.MediaItem>
            <S.MediaItem>
              <video
                ref={videoRef}
                muted
                controls={false}
                playsInline
                loop
                src={media.snippet?.imageUrl}
                preload="metadata"
                disablePictureInPicture
              />
            </S.MediaItem>
          </S.MediaGrid>
        );
      }

      case ProductMediaLayoutOption.OneAppOneHeroOneSnippet: {
        return (
          <S.MediaGrid>
            <S.MediaItem>{renderImage(media.app[0], undefined, false, true)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.hero[0], galleryAssets.length)}</S.MediaItem>
            <S.MediaItem>
              <video
                ref={videoRef}
                muted
                controls={false}
                playsInline
                loop
                src={media.snippet?.imageUrl}
                preload="metadata"
                disablePictureInPicture
              />
            </S.MediaItem>
          </S.MediaGrid>
        );
      }

      case ProductMediaLayoutOption.TwoAppTwoHero: {
        return (
          <S.MediaGrid>
            <S.MediaItem>{renderImage(media.app[0], undefined, false, true)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.app[1])}</S.MediaItem>
            <S.MediaItem>{renderImage(media.hero[0], galleryAssets.length)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.hero[1])}</S.MediaItem>
          </S.MediaGrid>
        );
      }

      case ProductMediaLayoutOption.ThreeAppOneHero: {
        return (
          <S.MediaGrid>
            <S.MediaItem>{renderImage(media.app[0], undefined, false, true)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.app[1])}</S.MediaItem>
            <S.MediaItem>{renderImage(media.app[2])}</S.MediaItem>
            <S.MediaItem>{renderImage(media.hero[0], galleryAssets.length)}</S.MediaItem>
          </S.MediaGrid>
        );
      }

      case ProductMediaLayoutOption.TwoAppOneHero: {
        return (
          <S.MediaGrid>
            <S.MediaItem>{renderImage(media.app[0], undefined, false, true)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.app[1])}</S.MediaItem>
            <S.MediaItem>{renderImage(media.hero[0], galleryAssets.length)}</S.MediaItem>
          </S.MediaGrid>
        );
      }

      case ProductMediaLayoutOption.TwoHeroOneApp: {
        return (
          <S.MediaGrid>
            <S.MediaItem>{renderImage(media.hero[0], undefined, false, true)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.hero[1])}</S.MediaItem>
            <S.MediaItem>{renderImage(media.app[0], media.app.length)}</S.MediaItem>
          </S.MediaGrid>
        );
      }

      case ProductMediaLayoutOption.MultipleHero: {
        return (
          <S.MediaGrid>
            {media.hero.slice(0, 4).map((x, i) => {
              return i === 2 ? (
                <S.MediaItem key={x.imageUrl}>{renderImage(x, 4)}</S.MediaItem>
              ) : (
                <S.MediaItem key={x.imageUrl}>{renderImage(x, undefined, false, i === 0)}</S.MediaItem>
              );
            })}
          </S.MediaGrid>
        );
      }

      case ProductMediaLayoutOption.OneHeroOnePack: {
        return (
          <S.MediaGrid>
            <S.MediaItem>{renderImage(media.hero[0], undefined, false, true)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.pack[0])}</S.MediaItem>
            <S.MediaItem>{renderImage(media.hero[0], 1)}</S.MediaItem>
          </S.MediaGrid>
        );
      }

      case ProductMediaLayoutOption.OneHero: {
        return (
          <S.MediaGrid>
            <S.MediaItem data-is-single={true}>{renderImage(media.hero[0], 1, true, true)}</S.MediaItem>
          </S.MediaGrid>
        );
      }

      case ProductMediaLayoutOption.OneAppOneHero: {
        return (
          <S.MediaGrid>
            <S.MediaItem>{renderImage(media.app[0], media.app.length)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.hero[0], undefined, false, true)}</S.MediaItem>
          </S.MediaGrid>
        );
      }

      case ProductMediaLayoutOption.OneHeroOneSnippet: {
        return (
          <S.MediaGrid>
            <S.MediaItem>{renderImage(media.hero[0], galleryAssets.length)}</S.MediaItem>
            <S.MediaItem>
              <video
                ref={videoRef}
                muted
                controls={false}
                playsInline
                loop
                src={media.snippet?.imageUrl}
                preload="metadata"
                disablePictureInPicture
              />
            </S.MediaItem>
          </S.MediaGrid>
        );
      }

      case ProductMediaLayoutOption.ThreeHeroOneApp: {
        return (
          <S.MediaGrid>
            <S.MediaItem>{renderImage(media.hero[0], undefined, false, true)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.hero[1], undefined, false, true)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.hero[2], undefined, false, true)}</S.MediaItem>
            <S.MediaItem>{renderImage(media.app[0], media.app.length)}</S.MediaItem>
          </S.MediaGrid>
        );
      }
    }
  };

  return (
    <>
      {showGallery ? (
        <Suspense
          fallback={
            <S.LightboxLoader>
              <Loader theme="white" />
            </S.LightboxLoader>
          }
        >
          {createPortal(<ImageZoomModal images={galleryAssets} onClose={() => setShowGallery(false)} />, document.body)}
        </Suspense>
      ) : null}

      {showEmbed && media.embed ? (
        <Suspense
          fallback={
            <S.LightboxLoader>
              <Loader theme="white" />
            </S.LightboxLoader>
          }
        >
          <ProductMediaLightbox
            translations={props.translations}
            media={[{ ...media.embed, url: `${media.embed.url}` }]}
            onClose={() => setShowEmbed(false)}
          />
        </Suspense>
      ) : null}

      {renderInner()}
    </>
  );
};

export default ProductMediaLayout;
